import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { useFeedback } from '../hooks/useFeedback';

interface FeedbackDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export function FeedbackDialog({ isOpen, onOpenChange }: FeedbackDialogProps) {
  const [feedback, setFeedback] = useState('');
  const [topic, setTopic] = useState('Comment');
  const { submitFeedback, isSubmitting, error } = useFeedback();

  const handleSubmit = async () => {
    const success = await submitFeedback(feedback, topic);
    if (success) {
      setFeedback('');
      setTopic('Comment');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-[#121212] border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-white">Provide Feedback</DialogTitle>
          <DialogDescription>
            Share your thoughts, suggestions, or report issues. Your feedback helps us improve.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="topic"
                className="block text-sm font-medium text-[#CECFD2] mb-1"
              >
                Topic
              </label>
              <select
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="flex w-full rounded-md border border-gray-700 bg-[#26222E] px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4E46FF] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-[#CECFD2]"
                disabled={isSubmitting}
              >
                <option>Comment</option>
                <option>Bug</option>
                <option>Feature Idea</option>
                <option>Request</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="feedback"
                className="block text-sm font-medium text-[#CECFD2] mb-1"
              >
                Your Feedback <span className="text-xs text-gray-400">(minimum 10 characters)</span>
              </label>
              <textarea
                id="feedback"
                className="flex w-full rounded-md border border-gray-700 bg-[#26222E] px-3 py-2 text-sm ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4E46FF] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[120px] text-[#CECFD2]"
                placeholder="Tell us what you think (at least 10 characters)..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-500">{error}</p>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            className="bg-[#26222E] text-[#CECFD2] hover:bg-[#342d3d]"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || feedback.trim().length < 10}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}